import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useHistory, Link } from "react-router-dom";
import Pagination from "react-js-pagination";
import Moment from "react-moment";
import moment from "moment";
import {
  Button,
  Card,
  CardHeader,
  ModalBody,
  Modal,
  Form,
  CardFooter,
  CardTitle,
  Input,
  Table,
  Container,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import ReactDatetime from "react-datetime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo, faTimes , faArrowCircleUp, faFileInvoice } from "@fortawesome/free-solid-svg-icons";
import ReactBSAlert from "react-bootstrap-sweetalert";
import transactionsService from "../../services/api/transaction-membership-service";
import projectsService from "../../services/api/projects-service";
import projectUsersService from "../../services/api/project-users-service";
import Projects from "../projects/Projects";
import AsyncSelect from "react-select/async";
import debounce from "lodash.debounce";
import InvoiceModal from "./InvoiceModal";

const RECORDS_PER_PAGE = 10;

const TRANSACTION_STATUS_OPTIONS = [
  { value: 0, label: "Initiated" },
  { value: 1, label: "Order Created" },
  { value: -1, label: "Order Creation Failed" },
  { value: 2, label: "Success" },
  { value: -2, label: "Failed" },
  { value: -3, label: "Cancelled" },
];

const TYPE_OPTIONS = [
  { value: "1", label: "One time" },
  { value: "2", label: "Recurring" },
];

const initialFormState = {
  user_id: "",
  transaction_id: "",
  from_date: "",
  to_date: "",
  status: [],
  verified_by: "",
  type: "",
  pg_subscription_id: "",
};

const createAlert = (type, message) => (
  <ReactBSAlert
    type={type}
    style={{ display: "block", marginTop: "-100px" }}
    title={type === "error" ? "Error" : "Success"}
    onConfirm={() => {}}
    onCancel={() => {}}
    confirmBtnBsStyle="success"
    confirmBtnText="OK"
    btnSize=""
  >
    {message}
  </ReactBSAlert>
);

const buildPlanLabelsFromConfig = (config = {}) => {
  const labelMap = {};
  const plansByCurrency = config?.plans || {};

  Object.values(plansByCurrency).forEach((tiers) => {
    Object.values(tiers || {}).forEach((periods) => {
      Object.values(periods || {}).forEach((plan) => {
        if (!plan?.plan_id) {
          return;
        }

        const heading = (plan.plan_heading || "").trim();
        const displayName = (plan.display_name || "").trim();
        const duration = (plan.plan_duration_label || "").trim();
        const labelParts = [];

        if (heading) {
          labelParts.push(heading);
        } else if (displayName) {
          labelParts.push(displayName);
        }

        if (
          duration &&
          !labelParts.some((part) => part.toLowerCase().includes(duration.toLowerCase()))
        ) {
          labelParts.push(duration);
        }

        labelMap[plan.plan_id] = labelParts.join(" - ") || plan.plan_id;
      });
    });
  });

  return labelMap;
};

const Transactions = () => {
  const history = useHistory();
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [projectDetails, setProjectDetails] = useState({});
  const [userNameArray, setUserNames] = useState([]);
  const [alert, setAlert] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [invoiceTransaction, setInvoiceTransaction] = useState(null);
  const [invoicePlanLabels, setInvoicePlanLabels] = useState({});

  const projectId = useMemo(
    () => new URLSearchParams(location.search).get("projectid"),
    [location.search]
  );

  const initFromURL = () => {
    const urlParams = new URLSearchParams(location.search);

    const formFromURL = { ...initialFormState };

    Object.keys(initialFormState).forEach((key) => {
      const paramValue = urlParams.get(key);

      if (paramValue !== null) {
        if (Array.isArray(initialFormState[key])) {
          formFromURL[key] = paramValue ? paramValue.split(",") : [];
        } else {
          formFromURL[key] = paramValue;
        }
      }
    });

    formFromURL.project_id = projectId || "";

    return formFromURL;
  };

  const [formState, setFormState] = useState(initFromURL);

  const [activePage, setActivePage] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    return parseInt(urlParams.get("page") || "1", 10);
  });

  const updateURL = useCallback(() => {
    const params = new URLSearchParams();

    if (projectId) {
      params.set("projectid", projectId);
    }

    Object.entries(formState).forEach(([key, value]) => {
      if (key !== "project_id") {
        if (Array.isArray(value) && value.length > 0) {
          params.set(key, value.join(","));
        } else if (value) {
          params.set(key, value);
        }
      }
    });

    if (activePage > 1) {
      params.set("page", activePage.toString());
    }

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [formState, activePage, projectId, history, location.pathname]);

  useEffect(() => {
    updateURL();
  }, [formState, activePage, updateURL]);

  // Create a current filters ref to hold the actively applied filters
  const activeFiltersRef = useRef({ ...initFromURL() });

  // Function to fetch transactions using the actively applied filters
  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    try {
      // Use the activeFiltersRef.current instead of formState directly
      const response = await transactionsService.getTransactions(
        activeFiltersRef.current,
        activePage,
        RECORDS_PER_PAGE
      );
      if (response.status && response.data) {
        setTransactions(response.data);
        const totalCount = await transactionsService.getTotalTransactions(
          activeFiltersRef.current
        );
        if (totalCount.status) {
          setTotalTransactions(totalCount.data.count);
        }
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setAlert(createAlert("error", "Failed to fetch transactions"));
    } finally {
      setIsLoading(false);
    }
  }, [activePage]); // Only depend on activePage so it refreshes on page change

  const debouncedFetchProjectUsers = debounce(
    async (inputValue, callback, projectId, setUserNames) => {
      try {
        const response = await projectUsersService.fetchUsers({
          project_id: projectId,
          search: inputValue,
          page: 1,
          page_size: "",
        });

        if (response?.status && response?.data?.users) {
          const options = response.data.users.map((user) => ({
            value: user.user_id,
            label: user.email
              ? `${user.name} (${user.email})`
              : `${user.name} ${user.phone_number}`,
            email: user.email,
          }));

          setUserNames(options); // for default display
          callback(options);     // return to AsyncSelect
        } else {
          setUserNames([]);
          callback([]);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setUserNames([]);
        callback([]);
      }
    },
    500
  );

  const fetchProjectUsers = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (!projectId) return resolve([]);
        debouncedFetchProjectUsers(inputValue, resolve, projectId, setUserNames);
      }),
    [projectId, setUserNames]
  );

  // Initial fetch only once when component mounts
  useEffect(() => {
    // Set initial active filters when component mounts
    activeFiltersRef.current = { ...formState };

    // Load initial data
    fetchTransactions();
  }, []); // Empty dependency array means run only once

  // useEffect(() => {
  //   fetchProjectUsers();
  // }, [fetchProjectUsers]);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!projectId) return;
      try {
        const result = await projectsService.getProjectDetails(projectId);
        if (result?.status) {
          setProjectDetails(result.data);
        }
      } catch (error) {
        console.error("Error fetching project details:", error);
      }
    };
    fetchProjectDetails();
  }, [projectId]);

  useEffect(() => {
    const fetchInvoicePlanLabels = async () => {
      if (!projectId) {
        setInvoicePlanLabels({});
        return;
      }

      try {
        const response = await projectsService.getProjectSubscriptionPlans(projectId);
        if (response?.status && response?.data?.config) {
          setInvoicePlanLabels(buildPlanLabelsFromConfig(response.data.config));
        } else {
          setInvoicePlanLabels({});
        }
      } catch (error) {
        console.error("Error fetching invoice plan labels:", error);
        setInvoicePlanLabels({});
      }
    };

    fetchInvoicePlanLabels();
  }, [projectId]);

  useEffect(() => {
    // Only fetch if we already have active filters (to avoid double fetching on initial mount)
    if (Object.keys(activeFiltersRef.current).length > 0) {
      fetchTransactions();
    }
  }, [activePage, fetchTransactions]);

  const handleFormChange = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Handle search explicitly calls fetchTransactions with updated filters
  const handleSearch = useCallback(
    (e) => {
      e?.preventDefault();
      setActivePage(1);

      // Update the active filters with current form state
      activeFiltersRef.current = { ...formState };

      // Then fetch with the updated filters
      fetchTransactions();
    },
    [fetchTransactions, formState]
  );

  const handleClickUser = useCallback(
    (projectId, userId) => {
      if (!projectId || !userId) return;

      history.push(
        `/dashboard/user-detailed-view?userid=${userId}&projectid=${projectId}`
      );
    },
    [history]
  );

  const handleClickSubs = useCallback(
    (projectId, pg_subscription_id) => {
      if (!projectId || !pg_subscription_id) return;

      history.push(
        `/membership/subscription?subscription_id=${pg_subscription_id}&projectid=${projectId}`
      );
    },
    [history]
  );

  const handleReset = useCallback(() => {
    const resetState = {
      ...initialFormState,
      project_id: projectId,
    };

    setFormState(resetState);
    setActivePage(1);

    const params = new URLSearchParams();
    if (projectId) {
      params.set("projectid", projectId);
    }

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });

    // Update active filters with reset values
    activeFiltersRef.current = { ...resetState };

    // Explicitly fetch after reset
    fetchTransactions();
  }, [projectId, history, location.pathname, fetchTransactions]);

  // Handle page change with explicit fetch
  const handlePageChange = useCallback((pageNumber) => {
    setActivePage(pageNumber);
    // No need to update activeFiltersRef as only the page is changing
    // Fetch will happen because of the activePage dependency in fetchTransactions
  }, []);

  const handleOpenModal = useCallback((transaction) => {
    setSelectedTransaction(transaction);
    setModalOpen(true);
  }, []);

  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return "Initiated";
      case -1:
        return "Order could not created";
      case 2:
        return "Success";
      case -2:
        return "Failed";
      case -3:
        return "Cancelled";
      default:
        return "";
    }
  };

  const generateInvoice = (transaction) => {
    setInvoiceTransaction(transaction);
    setIsInvoiceModalOpen(true);
  };

  const toggleModal = () => {
    setIsInvoiceModalOpen(false);
    setInvoiceTransaction(null);
  };

  const renderTransactionRows = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan="8" className="text-center">
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </td>
        </tr>
      );
    }

    if (!transactions.length) {
      return (
        <tr>
          <td colSpan="8" className="text-center">
            <CardTitle tag="h4">No Records Found</CardTitle>
          </td>
        </tr>
      );
    }

    return transactions.map((transaction, index) => {
      const rowNumber = RECORDS_PER_PAGE * (activePage - 1) + index + 1;

      return (
        <tr key={transaction.id}>
          <td className="text-left">{rowNumber}</td>
          <td
            style={{ cursor: "pointer" }}
            className="text-left "
            onClick={() =>
              handleClickUser(projectId, transaction?.user_id?._id)
            }
          >
            {transaction?.user_id?.email ||
            transaction?.user_id?.phone_number ? (
              <>
                {transaction?.user_id?.email && (
                  <div>{transaction.user_id.email}</div>
                )}
                {transaction?.user_id?.phone_number && (
                  <div>{transaction.user_id.phone_number}</div>
                )}
              </>
            ) : (
              "-"
            )}
          </td>

          <td className="text-left">
            {transaction?.currency === "INR" ? "₹ " : "$ "}
            {(transaction?.amount || 0) / 100}
          </td>
          <td
            style={{ cursor: "pointer", color: "#0077b6" }}
            onClick={() =>
              handleClickSubs(
                projectId,
                transaction?.subscription_id?.pg_subscription_id
              )
            }
          >
            {transaction?.subscription_id?.pg_subscription_id || "-"}
          </td>

          <td className="text-left">{transaction.pg_txn_id || "-"}</td>
          <td className="text-left">
            <Moment format="DD MMM YYYY, hh:mm A">
              {transaction.createdat}
            </Moment>
          </td>
          <td className="text-left">
            {transaction.type === "1" ? "One time" : "Recurring"}
          </td>
          <td className="text-left">{getStatusText(transaction.status)}</td>
          <td>
            <div className="d-flex align-items-center gap-2">
            <Button
              color="info"
              size="sm"
              onClick={() => handleOpenModal(transaction)}
            >
              <FontAwesomeIcon icon={faInfo} />
            </Button>
            {transaction.status === 2 && (
              <Button
                color="success"
                size="sm"
                title="Generate Invoice"
                className="rounded-circle d-flex justify-content-center align-items-center p-0"
                style={{ width: '32px', height: '32px' }}
                onClick={() => generateInvoice(transaction)}
              >
                <FontAwesomeIcon icon={faFileInvoice} />
              </Button>
            )}
            </div>
          </td>
        </tr>
      );
    });
  };

  if (!projectId) {
    return <Projects navigate="membership/transaction" feature="transaction"/>;
  }

  return (
    <div className="content">
      {alert}
      <Container>
        <Card className="card-signup">
          <CardHeader className="bg-white p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
               <div className="d-flex align-items-center">
                                  <CardTitle tag="h4">Transaction Dashboard</CardTitle>
                                  <span
                                    className="text-muted "
                                    style={{ marginLeft: "5px", marginTop: "5px" }}
                                  >
                                    {projectDetails.name
                                      ? `(${projectDetails.name})`
                                      : "(Project Name)"}
                                  </span>
                                </div>
              <div>
            
              <Link
    to={`sales-membership?projectid=${projectId}`}
    className="d-flex align-items-center text-info border rounded px-3 py-1 mr-2"
    title="Go to Sales Dashboard"
    style={{ textDecoration: 'none' }}
  >
    <span className="mr-2">Sales Dashboard</span>
    <FontAwesomeIcon   style={{ cursor: 'pointer', marginLeft: '10px' }} icon={faArrowCircleUp} />
  </Link>
          
              </div>
            </div>

            <Form id="transactionfilters" onSubmit={handleSearch}>
              <Row>
                <Col md="3">
                  <AsyncSelect
                    isClearable
                    placeholder="Select User"
                    defaultOptions={false}
                    loadOptions={fetchProjectUsers} // now debounced
                    onChange={(option) =>
                      handleFormChange("user_id", option?.value || "")
                    }
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? "No users found" : "Search to get options"
                    }
                    value={
                      formState.user_id
                        ? {
                          value: formState.user_id,
                          label:
                            userNameArray.find((u) => u.value === formState.user_id)?.label ||
                            "Selected User",
                        }
                        : null
                    }
                  />
                </Col>

                <Col md="3">
                  <ReactDatetime
                    value={formState.from_date}
                    onChange={(date) => handleFormChange("from_date", date._d)}
                    inputProps={{
                      className: "form-control",
                      placeholder: "From Date",
                    }}
                  />
                </Col>

                <Col md="3">
                  <ReactDatetime
                    value={formState.to_date}
                    onChange={(date) => handleFormChange("to_date", date._d)}
                    inputProps={{
                      className: "form-control",
                      placeholder: "To Date",
                    }}
                  />
                </Col>

                <Col md="3">
                  <Input
                    type="text"
                    placeholder="Transaction Id"
                    value={formState.transaction_id}
                    onChange={(e) =>
                      handleFormChange("transaction_id", e.target.value)
                    }
                  />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="3">
                  <Select
                    isMulti
                    isClearable
                    placeholder="Select Status"
                    options={TRANSACTION_STATUS_OPTIONS}
                    value={TRANSACTION_STATUS_OPTIONS.filter((option) =>
                      formState.status.includes(option.value)
                    )}
                    onChange={(selectedOptions) =>
                      handleFormChange(
                        "status",
                        selectedOptions
                          ? selectedOptions.map((opt) => opt.value)
                          : []
                      )
                    }
                  />
                </Col>

                <Col md="3">
                  <Select
                    isClearable
                    placeholder="Transaction Type"
                    options={TYPE_OPTIONS}
                    value={
                      TYPE_OPTIONS.find(
                        (option) => option.value === formState.type
                      ) || null
                    }
                    onChange={(option) =>
                      handleFormChange("type", option?.value)
                    }
                  />
                </Col>
                <Col md="3">
                  <Input
                    type="text"
                    placeholder="Pg Membership Id"
                    value={formState.pg_subscription_id}
                    onChange={(e) =>
                      handleFormChange("pg_subscription_id", e.target.value)
                    }
                  />
                </Col>
                <Col md="3">
                  <Button className="btn-round mr-2" color="info" type="submit">
                    Search
                  </Button>
                  <Button
                    className="btn-round"
                    color="warning"
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Form>

            <hr />
            {totalTransactions > 0 ? (
              <Row>
                <Col md="6">
                  <div style={{ float: "right", margin: "5px" }}>
                    <b>
                      {" "}
                      {(activePage - 1) * RECORDS_PER_PAGE + 1} -{" "}
                      {Math.min(
                        activePage * RECORDS_PER_PAGE,
                        totalTransactions
                      )}{" "}
                      of {totalTransactions}
                    </b>
                  </div>
                </Col>
                <Col md="6">
                  <div style={{ float: "left" }}>
                    <Pagination
                      activePage={activePage}
                      itemsCountPerPage={RECORDS_PER_PAGE}
                      totalItemsCount={totalTransactions}
                      pageRangeDisplayed={3}
                      onChange={handlePageChange}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                </Col>
              </Row>
            ) : null}

            <Table responsive>
              <thead className="text-primary">
                <tr>
                  <th className="text-left">S.no.</th>
                  <th className="text-left">User Email or Phone No.</th>
                  <th className="text-left">Transaction Amount</th>
                  <th className="text-left">Membership ID</th>
                  <th className="text-left">Transaction Id</th>
                  <th className="text-left">Transaction Date</th>
                  <th className="text-left">Type</th>
                  <th className="text-center">Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>{renderTransactionRows()}</tbody>
            </Table>
            <Modal
              isOpen={modalOpen}
              toggle={() => setModalOpen(false)}
              size="md"
            >
              <ModalBody className="p-3 position-relative">
                {/* Close Button */}
                <div
                  className="position-absolute d-flex align-items-center justify-content-center"
                  style={{
                    right: "12px",
                    top: "8px",
                    cursor: "pointer",
                    zIndex: 1,
                    backgroundColor: "red",
                    borderRadius: "50%",
                    width: "25px",
                    height: "25px",
                  }}
                  onClick={() => setModalOpen(false)}
                >
                  <FontAwesomeIcon icon={faTimes} className="text-white" />
                </div>

                {/* Transaction Details Section */}
                <div className="mb-3">
                  <h6 className="mb-2">Transaction Details</h6>
                  <div className="border rounded">
                    <Row className="g-0">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">
                          Transaction ID
                        </div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.id || "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">Order ID</div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.order_id || "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">
                          PG Transaction ID
                        </div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.pg_txn_id || "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">
                          PG Transaction Message
                        </div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.pg_txn_message || "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">Amount</div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.currency === "INR"
                            ? "₹ "
                            : "$ "}
                          {(selectedTransaction?.amount || 0) / 100}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">Status</div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {getStatusText(selectedTransaction?.status)}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">
                          Transaction Date
                        </div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          <Moment format="DD MMM YYYY, hh:mm A">
                            {selectedTransaction?.createdat}
                          </Moment>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div>
                  <h6 className="mb-2">Membership Details</h6>
                  <div className="border rounded">
                    <Row className="g-0">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">
                          Membership ID
                        </div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.subscription_id
                            ?.pg_subscription_id || "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">Paid Count</div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.subscription_id?.paid_count ||
                            "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">Status</div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.subscription_id?.status || "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">Type</div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.subscription_id?.total_count ===
                          1
                            ? "One Time"
                            : selectedTransaction?.subscription_id?.total_count
                            ? "Recurring"
                            : "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">Share Url</div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.subscription_id?.share_url ||
                            "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">
                          Membership Start Date
                        </div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.subscription_id?.start_at
                            ? moment
                                .unix(
                                  selectedTransaction?.subscription_id?.start_at
                                )
                                .format("DD MMM YYYY, hh:mm A")
                            : "-"}
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-0 border-top">
                      <Col xs="5" className="p-2 bg-light">
                        <div className="fw-semibold text-dark">
                          Membership End Date
                        </div>
                      </Col>
                      <Col className="p-2">
                        <div className="fw-semibold">
                          {selectedTransaction?.subscription_id?.end_at
                            ? moment
                                .unix(
                                  selectedTransaction?.subscription_id?.end_at
                                )
                                .format("DD MMM YYYY, hh:mm A")
                            : "-"}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </ModalBody>
            </Modal>
            <InvoiceModal
              isOpen={isInvoiceModalOpen}
              toggle={toggleModal}
              transaction={invoiceTransaction}
              planLabels={invoicePlanLabels}
            />
          </CardHeader>
          <CardFooter>
            <hr />
          </CardFooter>
        </Card>
      </Container>
    </div>
  );
};

export default Transactions;
